import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Avatar,
    TextField,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Pagination,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';

const Container = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem',
    backgroundColor: '#1c1c1c',
    minHeight: '100vh',
});

const PaginationWrapper = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
});


const Title = styled(Typography)({
    fontSize: '1.8rem',
    color: '#fff',
    fontWeight: 'bold',
});

const SearchBar = styled(TextField)({
    marginBottom: '20px',
    width: '100%',
    backgroundColor: '#202020',
    borderRadius: '8px',
    '& .MuiInputBase-root': { color: '#fff' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#12c099' },
    '& .MuiInputLabel-root': { color: '#12c099' },
});

const TableWrap = styled(TableContainer)({
    backgroundColor: '#202020',
    borderRadius: '12px',
});

const Cell = styled(TableCell)({
    padding: '10px 16px',
    fontSize: '0.9rem',
    color: '#fff',
    whiteSpace: 'nowrap',
});

const Couriers = () => {
    const [couriers, setCouriers] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');

    const rowsPerPage = 25;

    useEffect(() => {
        const fetchCouriers = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/couriers/list`
                );
                setCouriers(res.data.data || []);
            } catch (error) {
                console.error('Error fetching couriers:', error);
                setCouriers([]);
            }
        };
        fetchCouriers();
    }, []);

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const filtered = couriers.filter(c =>
        (c.name || '').toLowerCase().includes(search.toLowerCase())
    );

    const sorted = filtered.sort((a, b) => {
        let aVal;
        let bVal;

        if (orderBy === 'deliveryCount') {
            aVal = a.deliveries?.length || 0;
            bVal = b.deliveries?.length || 0;
        } else {
            aVal = a[orderBy] || '';
            bVal = b[orderBy] || '';
        }

        if (typeof aVal === 'number') {
            return order === 'asc' ? aVal - bVal : bVal - aVal;
        }

        return order === 'asc'
            ? aVal.toString().localeCompare(bVal.toString())
            : bVal.toString().localeCompare(aVal.toString());
    });

    const paginated = sorted.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    return (
        <Container>
            <Title>Couriers</Title>

            <SearchBar
                label="Search Couriers"
                onChange={(e) => setSearch(e.target.value)}
            />

            <TableWrap component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <Cell>
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={order}
                                    onClick={() => handleSort('name')}
                                >
                                    Name
                                </TableSortLabel>
                            </Cell>

                            <Cell>
                                <TableSortLabel
                                    active={orderBy === 'city'}
                                    direction={order}
                                    onClick={() => handleSort('city')}
                                >
                                    Location
                                </TableSortLabel>
                            </Cell>

                            <Cell>
                                <TableSortLabel
                                    active={orderBy === 'vehicleType'}
                                    direction={order}
                                    onClick={() => handleSort('vehicleType')}
                                >
                                    Vehicle
                                </TableSortLabel>
                            </Cell>

                            <Cell>
                                <TableSortLabel
                                    active={orderBy === 'deliveryCount'}
                                    direction={order}
                                    onClick={() => handleSort('deliveryCount')}
                                >
                                    Deliveries
                                </TableSortLabel>
                            </Cell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginated.map(courier => (
                            <TableRow key={courier.firebaseUid}>
                                <Cell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar
                                            src={courier.avatarImageUrl}
                                            sx={{ width: 28, height: 28, mr: 1 }}
                                        >
                                            {(courier.name || ' ')[0]}
                                        </Avatar>

                                        <Link
                                            to={`/courier/${courier.firebaseUid}`}
                                            style={{ color: '#12c099', textDecoration: 'none' }}
                                        >
                                            {courier.name}
                                        </Link>
                                    </Box>
                                </Cell>

                                <Cell>{courier.city}, {courier.state}</Cell>
                                <Cell>{courier.vehicleType || 'N/A'}</Cell>
                                <Cell>{courier.deliveries?.length || 0}</Cell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableWrap>

            <PaginationWrapper>
                <Pagination
                    count={Math.ceil(filtered.length / rowsPerPage)}
                    page={page}
                    onChange={(e, v) => setPage(v)}
                    sx={{
                        '& .MuiPaginationItem-root': {
                            color: '#12c099',
                        },
                    }}
                />
            </PaginationWrapper>

        </Container>
    );
};

export default Couriers;
